import React, { useContext, useMemo, useState } from 'react';
import { useQuery } from '@apollo/client';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { FormInput, FormSwitch } from 'components/form';
import { formatMoney, getProductVendorName } from 'utils';
import { MUITableSorting } from 'components/shared/MUITable';
import { AddCircleOutline, Search } from '@mui/icons-material';
import { CommonDataContext } from 'contexts/CommonDataContext';
import { Box, Button, InputAdornment, Stack } from '@mui/material';
import { AftermarketLicenceType } from '@trnsact/trnsact-shared-types';
import { CardContainer } from 'components/shared/CardContainer/CardContainer';
import { portalConfigurationsSelectors } from 'redux/portalConfigurationReducer';

import { aftermarketProductsApi } from '../../api';
import { useAftermarketProductsSearch } from '../../hooks';
import { AftermarketProductsTable, AftermarketProductTableEntity } from './AftermarketProductsTable';

interface Criteria {
  event?: { params?: { DEALERCOST?: number } };
}

const getProductCost = (product: Record<string, unknown>): string => {
  const criteria = product.criteria as Criteria[] | undefined;
  const costUnformatted = criteria?.[0]?.event?.params?.DEALERCOST ?? 0;
  return costUnformatted ? `$ ${formatMoney(costUnformatted)}` : 'N/A';
};

const getSortValue = (product: Record<string, unknown>, key: string): string | number | Date => {
  if (key === 'createdDateTime') {
    return new Date(parseInt(product[key] as string));
  }
  return (product[key] as string)?.toString().toLowerCase() ?? '';
};

export const ListAftermarketProducts = () => {
  const history = useHistory();

  const account = useSelector<any, any>(state => state.account);
  const canWrite = useSelector(portalConfigurationsSelectors.hasAftermarketWriteAccess);
  const aftermarketLicenceType = useSelector(portalConfigurationsSelectors.aftermarketLicenceType);

  const { isSuperAdminOrSingleAccount } = useContext(CommonDataContext);

  const [sorting, setSorting] = useState<{ key: string; value: 'asc' | 'desc' }>({
    key: 'productName',
    value: 'asc',
  });

  const [activeOnly, setActiveOnly] = useState(true);
  const isPartnerAccount = account.accountType === 'Partner';

  const { data, loading } = useQuery(aftermarketProductsApi.list, {
    variables: {
      activeOnly,
      accountId: account.id,
    },
  });

  const mappedProducts = useMemo(
    () =>
      (data?.aftermarketProducts ?? []).map((product: Record<string, unknown>) => ({
        ...product,
        productCost: getProductCost(product),
        vendorName: getProductVendorName(product, account.name),
      })),
    [account.name, data?.aftermarketProducts]
  );

  const { searchText, setSearchText, filteredProducts } = useAftermarketProductsSearch({
    products: mappedProducts,
  });

  const sortedProducts = useMemo(() => {
    return [...filteredProducts].sort((a, b) => {
      const aVal = getSortValue(a, sorting.key);
      const bVal = getSortValue(b, sorting.key);

      if (aVal < bVal) return sorting.value === 'asc' ? -1 : 1;
      if (aVal > bVal) return sorting.value === 'asc' ? 1 : -1;
      return 0;
    });
  }, [filteredProducts, sorting]);

  const handleSortingChange: MUITableSorting['onChange'] = nextValue => {
    setSorting(nextValue);
  };

  const handleNavigateToAddPage = () => {
    history.push('/aftermarket/add');
  };

  const tableData: AftermarketProductTableEntity[] = useMemo(
    () =>
      sortedProducts.map((product: any) => ({
        id: product.aftermarketProductId,
        vendorName: product.vendorName ?? '-',
        productName: product.productName ?? '-',
        productType: product.productType ?? '-',
        productCost: product.productCost ?? '-',
        productCategory: product.productCategory ?? '-',
        createdByName: product.createdBy?.name ?? 'N/A',
        productDescriptionExternal: product.productDescriptionExternal ?? '-',
        createdDateTime: product.createdDateTime ? Number(product.createdDateTime) : '-',
      })),
    [sortedProducts]
  );

  const isAddProductBtnVisible =
    ([AftermarketLicenceType.Enterprise, AftermarketLicenceType.Pro].includes(aftermarketLicenceType) && canWrite) ||
    isSuperAdminOrSingleAccount;

  return (
    <CardContainer
      title="Products"
      style={{ height: 'calc(100% - 4.2rem)' }}
      actionBtn={
        isAddProductBtnVisible && (
          <Button size="small" variant="contained" startIcon={<AddCircleOutline />} onClick={handleNavigateToAddPage}>
            Add Aftermarket Product
          </Button>
        )
      }
    >
      <Stack direction="row" alignItems="center" gap="1rem">
        <FormInput
          variant="outlined"
          value={searchText}
          placeholder="Search"
          onChange={value => setSearchText(value)}
          slotProps={{
            input: {
              endAdornment: (
                <InputAdornment position="end">
                  <Search />
                </InputAdornment>
              ),
            },
          }}
        />

        <FormSwitch
          checked={activeOnly}
          label="Only Active Products"
          onChange={({ target }) => setActiveOnly(target.checked)}
        />
      </Stack>

      <Box sx={{ height: 'calc(100% - 7rem)' }}>
        <AftermarketProductsTable
          products={tableData}
          isLoading={loading}
          isPartnerAccount={isPartnerAccount}
          sorting={{ ...sorting, onChange: handleSortingChange }}
          onProductClick={productId => history.push(`/aftermarket/edit/${productId}`)}
        />
      </Box>
    </CardContainer>
  );
};
